import React from 'react';
import { matchPath, useLocation, useParams } from 'react-router';
import { AppBar, Box, IconButton, SvgIconTypeMap, Toolbar, Typography, styled } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getOnCloseRedirectUrl, setOnCloseRedirectUrl } from 'components/user/localAuth';
import { CONTENT_MAX_WIDTH, NAVBAR_ICON_BUTTON_PADDING } from 'config/constants';
import { isString } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { AppBarUserMenu } from './AppBarUserMenu';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.primary.contrastText,
    color: theme.palette.primary.main
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    width: 'calc(100% - 96px)',
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
    color: theme.palette.titleTextColour
}));

const EmptyButton = styled('div')(() => ({
    width: '48px',
    height: 1
}));

const BackButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main
}));

const UserAvatar = styled('img')(() => ({
    width: '24px',
    height: '24px',
    borderRadius: '50%'
}));

const UserInitials = styled('span')(({ theme }) => ({
    fontSize: theme.spacing(1.5),
    fontWeight: theme.typography.fontWeightBold,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%'
}));

const StyledToolbar = styled(Toolbar)({
    width: '100%',
    margin: '0 auto',
    maxWidth: `${CONTENT_MAX_WIDTH + NAVBAR_ICON_BUTTON_PADDING}px`
});

const ColorBox = styled(Box)(({ theme }) => ({
    color: theme.palette.titleTextColour
}));

interface IMuiAppBarProps {
    title: string | React.ReactNode;
    disableBack?: boolean;
    disabledMenu?: boolean;
    onBack?: () => void;
    icon?: OverridableComponent<SvgIconTypeMap>;
    elevation?: number;
    ignoreGA?: boolean;
    onBackEventName?: string;
}

export const MuiAppBar: React.FC<React.PropsWithChildren<IMuiAppBarProps>> = ({
    title,
    children,
    disableBack,
    disabledMenu,
    onBack,
    elevation = 1,
    icon,
    ignoreGA,
    onBackEventName
}) => {
    const { merchantId } = useParams();
    const appBarRef = React.useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
    const [isAvatarAvailable, setIsAvatarAvailable] = React.useState<boolean>(true);
    const { push, navigate } = useLocalHistory();
    const { pathname } = useLocation();
    const { isLoggedIn, user } = useAuth();
    const { logUserEvent } = useGAHelpers();
    const isAuthorizationUrl = React.useMemo(() => {
        const authorizationRoutes = [
            ROUTES.USER.LOGIN,
            ROUTES.USER.REGISTER,
            ROUTES.USER.DETAILS,
            ROUTES.USER.REWARDS,
            ROUTES.USER.ACTIVITY,
            ROUTES.USER.PAYMENT_METHODS
        ];
        return authorizationRoutes.some(item => !!matchPath(pathname, item));
    }, [pathname]);
    const handleLeftClick = React.useCallback(() => {
        if (!ignoreGA) {
            if (icon) {
                logUserEvent(onBackEventName ?? EGAEventName.CustomCloseIconClick);
            } else {
                logUserEvent(onBackEventName ?? EGAEventName.BackIconClick);
            }
        }
        if (typeof onBack === 'function') {
            onBack();
        } else if (isAuthorizationUrl) {
            if (merchantId) {
                const redirectUrl = getOnCloseRedirectUrl(merchantId);
                if (redirectUrl) {
                    const [path, queryparam] = redirectUrl.split('?');
                    push(path, {}, queryparam);
                    setOnCloseRedirectUrl('', merchantId);
                } else {
                    navigate(-1);
                }
            }
        } else {
            navigate(-1);
        }
    }, [
        ignoreGA,
        onBack,
        isAuthorizationUrl,
        icon,
        logUserEvent,
        onBackEventName,
        merchantId,
        push,
        navigate
    ]);
    const handleRightClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl((value?: HTMLElement) => (!!value ? undefined : e.currentTarget));
    }, []);
    const closeMenu = React.useCallback(() => setAnchorEl(undefined), []);

    const Icon = React.useMemo(() => (icon ? icon : ArrowBackIcon), [icon]);
    const menuId = 'app-bar-menu-id';
    const handleError = React.useCallback(() => {
        setIsAvatarAvailable(false);
    }, []);
    const userInitials = React.useMemo(() => {
        if (!user || (!user.firstName && !user.lastName)) {
            return undefined;
        }
        return user.firstName[0] + user.lastName[0];
    }, [user]);
    return (
        <>
            <StyledAppBar position="fixed" elevation={elevation || 0}>
                <StyledToolbar ref={appBarRef} disableGutters variant="dense">
                    {!disableBack ? (
                        <BackButton onClick={handleLeftClick} size="large">
                            <Icon />
                        </BackButton>
                    ) : (
                        <EmptyButton />
                    )}
                    {isString(title) ? (
                        <StyledTitle variant="h6">{title}</StyledTitle>
                    ) : (
                        <ColorBox flex={1}>{title}</ColorBox>
                    )}
                    {children}
                    {!disabledMenu && (
                        <IconButton
                            aria-label="show more"
                            aria-controls={menuId}
                            aria-haspopup="true"
                            onClick={handleRightClick}
                            color="inherit"
                            size="large"
                        >
                            {isLoggedIn &&
                                (isAvatarAvailable ? (
                                    <UserAvatar
                                        src={`${process.env.MEDIA_URL}/users/avatars/${user?.id}.jpg`}
                                        onError={handleError}
                                    />
                                ) : (
                                    <UserInitials>{userInitials}</UserInitials>
                                ))}
                            {!isLoggedIn && <AccountCircle sx={{ color: 'primary.main' }} />}
                        </IconButton>
                    )}
                </StyledToolbar>
            </StyledAppBar>
            {appBarRef.current && <div style={{ height: appBarRef.current.clientHeight }} />}
            {!appBarRef.current && <Toolbar disableGutters variant="dense" sx={{ height: '48px' }} />}
            {!!anchorEl && !disabledMenu && (
                <AppBarUserMenu anchorEl={anchorEl} menuId={menuId} onClose={closeMenu} />
            )}
        </>
    );
};
